import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { addNotification } from 'notifications';
import { api } from 'api/v1';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import {
  resolveProfileType,
  type ProfileType,
} from 'utils/resolveProfileType';

import type { IProfile, IProfileParams } from './types';

export const fetchProfileByType = async (profileType: ProfileType) => {
  const response = await api.get(`/profile/${profileType}`, {
    skipGlobalErrorHandler: true,
  });
  return response.data;
};

export const fetchCurrentProfile = async (profileType?: ProfileType) => {
  const resolvedType = profileType ?? resolveProfileType();
  if (!resolvedType) {
    throw new Error('Profile type not found');
  }
  return fetchProfileByType(resolvedType);
};

function useGetProfileReview() {
  return useQuery<IProfile>({
    queryKey: ['profile-review'],
    queryFn: () => api.get('/profile/review').then((res) => res.data),
  });
}

export const useRevertProfileDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/profile/verification-draft/revert');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verification-status'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { data: session } = useAuthSession();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const profileType = resolveProfileType({
        sessionRoles: session?.roles,
        pathname,
      });
      if (!profileType) {
        throw new Error('Profile type not found');
      }

      const response = await api.put(`/profile/${profileType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    onSuccess: (data) => {
      const profileType = resolveProfileType({
        sessionRoles: session?.roles,
        pathname,
      });
      if (profileType) {
        queryClient.setQueryData(['profile', profileType], (old: any) => ({
          ...old,
          ...data,
        }));
        queryClient.invalidateQueries({ queryKey: ['profile', profileType] });
        queryClient.invalidateQueries({ queryKey: ['profile', 'student'] });
        queryClient.invalidateQueries({ queryKey: ['photos-full'] });
        queryClient.invalidateQueries({ queryKey: ['tutor-profile-readiness'] });
        queryClient.invalidateQueries({ queryKey: ['student-profile-readiness'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['verification-status'] });
      }
    },

    onError: (error) => {
      console.error('Failed to update profile:', error);
    },
  });
};

function useGetProfiles(params?: IProfileParams) {
  return useQuery<IProfile[]>({
    queryKey: ['profiles', params],
    queryFn: () => api.get('/profiles', { params }).then((res) => res.data),
  });
}

function useGetCurrentProfile() {
  const { pathname } = useLocation();
  const { data: session } = useAuthSession();
  const profileType = resolveProfileType({
    sessionRoles: session?.roles,
    pathname,
  });

  return useQuery<IProfile>({
    queryKey: ['profile', profileType],
    queryFn: () => fetchProfileByType(profileType!),
    enabled: Boolean(profileType) && isAuthSessionActive(session),
    retry: false,
  });
}

function useGetProfileById(id: number) {
  return useQuery<IProfile>({
    queryKey: ['profile', id],
    queryFn: () => api.post(`/profiles/${id}`).then((res) => res.data),
  });
}

function useUpdateProfileById(id: IProfile['id']) {
  const queryClient = useQueryClient();

  return useMutation<IProfile, Error, IProfile>({
    mutationFn: (data: IProfile) =>
      api.put(`/profiles/${id}`, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', id] });
      addNotification('Профиль успешно обновлен', 'success');
    },
    onError: () => {
      addNotification('Ошибка при обновлении профиля', 'error');
    },
  });
}

export {
  useGetProfileReview,
  useGetProfileById,
  useUpdateProfileById,
  useGetCurrentProfile,
  useGetProfiles,
};
